import { AlertTriangle, Info, Sparkles } from 'lucide-react'
import clsx from 'clsx'
import { Button } from '@/components/common/Button'
import { useRunAdvisor } from '@/hooks/usePatching'
import styles from './Patching.module.css'

export const ADVISOR_LABEL = 'AI-generated advice — does not affect approvals or gates'
export const ADVISOR_UNAVAILABLE = 'Advisor unavailable — the patch process is unaffected.'

/**
 * Read-only AI advice. Everything rendered here is advisory text; nothing on this panel
 * feeds an approval, a gate or any other control.
 */
export function AdvisorPanel({ campaignId }: { campaignId: string }) {
  const advisor = useRunAdvisor()
  const result = advisor.data

  return (
    <div data-testid="advisor-panel">
      <div className={styles.advisorBanner} data-testid="advisor-label">
        <Info size={14} aria-hidden="true" />
        {ADVISOR_LABEL}
      </div>

      <div className={styles.actionRow}>
        <Button
          icon={<Sparkles size={14} />}
          onClick={() => advisor.mutate(campaignId)}
          loading={advisor.isPending}
        >
          {result ? 'Ask again' : 'Get advice'}
        </Button>
      </div>

      {advisor.isError && (
        <div className={clsx(styles.alert, styles.alertWarning)} role="alert" data-testid="advisor-error" style={{ marginTop: 12 }}>
          <AlertTriangle size={16} className={styles.alertIcon} />
          <span>
            {advisor.error.message} {ADVISOR_UNAVAILABLE}
          </span>
        </div>
      )}

      {result && !result.available && (
        <div className={clsx(styles.alert, styles.alertInfo)} data-testid="advisor-unavailable" style={{ marginTop: 12 }}>
          <Info size={16} className={styles.alertIcon} />
          <span>{ADVISOR_UNAVAILABLE}</span>
        </div>
      )}

      {result?.available && (
        <div className={styles.stack} style={{ marginTop: 12 }} data-testid="advisor-result">
          {result.summary && (
            <div>
              <h4 className={styles.sectionTitle}>Summary</h4>
              <p className={styles.bodyText}>{result.summary}</p>
            </div>
          )}
          {result.priorities.length > 0 && (
            <div>
              <h4 className={styles.sectionTitle}>Priorities</h4>
              <ul className={styles.advisorList} data-testid="advisor-priorities">
                {result.priorities.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            </div>
          )}
          {result.missing_prerequisites.length > 0 && (
            <div>
              <h4 className={styles.sectionTitle}>Missing prerequisites</h4>
              <ul className={styles.advisorList} data-testid="advisor-prerequisites">
                {result.missing_prerequisites.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            </div>
          )}
          {result.invocation_id !== null && (
            <span className={styles.hint}>Advisor invocation {String(result.invocation_id)} (logged)</span>
          )}
        </div>
      )}
    </div>
  )
}
